import { useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ShieldCheck, Store, Truck } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { Button, ErrorBlock, Img, Price, QtyStepper, Spinner, fen } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { useAddToCart, useProduct } from "../../api/queries.ts";
import { readPurchaseAs, readViewAs, writeIdentity } from "../../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 商品详情
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **左图廊 + 右购买区 + 下方分栏详情。**
 *    手机版是"大图 → 标题 → 价 → 规格 → 参数 → 详情"一路往下滚 ——
 *    在 1440 的屏幕上照搬，一张图就占满整屏，价格和规格全被推到第二屏，
 *    用户得滚一屏才知道多少钱、有哪些规格。
 *
 * ⚠️ 缩略图在网页上**竖排在大图左侧**：横排会挤掉大图的宽度，
 *    而鼠标从上往下扫比横向拖顺手。
 * ⚠️ 参数/安装/详情做成**标签页**：三段全铺开的话页面会拉得很长，
 *    而看参数的人和看详情的人是两拨。
 */

const TABS = [
  { key: "attrs", label: "商品参数" },
  { key: "install", label: "安装说明" },
  { key: "desc", label: "商品详情" },
] as const;

export function DesktopProductDetail() {
  const { id } = useParams({ from: "/products/$id" });
  const search = useSearch({ from: "/products/$id" }) as { shopId?: number };
  const navigate = useNavigate();

  const { data, isPending, error } = useProduct(id, search.shopId);
  const addToCart = useAddToCart();
  /** 加购成功的短暂反馈 —— 不跳走就必须给个回应，否则不知道点上了没有。 */
  const [added, setAdded] = useState(false);

  const [imgIdx, setImgIdx] = useState(0);
  const [skuId, setSkuId] = useState<number | null>(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("attrs");

  /**
   * 🔴 **预览「我卖的价」时不能下单。**
   *    那个价是客户看到的，按它下单会生成一张"自己卖给自己"的单。
   *    但不给出路的话用户就卡在这儿了 —— 点一下直接切回「我自己」。
   */
  const previewing = readPurchaseAs() !== null && readViewAs() === "shop";

  if (isPending)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );
  if (error || !data)
    return (
      <DesktopShell>
        <ErrorBlock error={error ?? new Error("商品不存在")} />
      </DesktopShell>
    );

  const sku = data.skus.find((s) => s.id === skuId) ?? data.skus[0];
  const canBuy = Boolean(sku?.available);

  const onAdd = async () => {
    if (!sku) return;
    await addToCart.mutateAsync({ skuId: sku.id, quantity: qty });
    /**
     * ⚠️ **加完留在原地，不跳购物车。** 建材一次要买好几样，
     *    每加一件就被弹走的话，加三件要来回六次。
     */
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  /**
   * 🔴 **「立即下单」不经过购物车** —— 直接把这一件带到结算页。
   *    先加进购物车再结算的话，购物车里上次没买完的东西会一起被下单。
   */
  const onBuyNow = () => {
    if (!sku) return;
    navigate({ to: "/checkout", search: { skuId: sku.id, qty } });
  };

  return (
    <DesktopShell>
      <Crumbs
        items={[
          { label: "首页", to: "/" },
          { label: "全部商品", to: "/products" },
          { label: data.title },
        ]}
      />

      <div className="grid grid-cols-[minmax(0,1fr)_420px] items-start gap-10">
        {/* ── 左：图廊 ── */}
        <div className="flex gap-4">
          {data.imageUrls.length > 1 && (
            /* ⚠️ 竖排缩略图：横排会挤掉大图宽度，鼠标从上往下扫也更顺手。 */
            <div className="flex w-[72px] shrink-0 flex-col gap-2">
              {data.imageUrls.map((url, i) => (
                <button
                  key={url}
                  onMouseEnter={() => setImgIdx(i)}
                  onClick={() => setImgIdx(i)}
                  className={cn(
                    "aspect-square overflow-hidden rounded-lg border-2 transition-colors",
                    i === imgIdx ? "border-brand-500" : "border-transparent hover:border-border",
                  )}
                >
                  <img src={url} alt="" className="size-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-border bg-card">
            <Img
              src={data.imageUrls[imgIdx]}
              alt={data.title}
              className="aspect-square w-full object-cover"
            />
          </div>
        </div>

        {/* ── 右：购买区 ── */}
        <div className="space-y-5">
          <div>
            <h1 className="text-[24px] leading-snug font-medium">{data.title}</h1>
            {data.subtitle && (
              <p className="mt-1.5 text-[15px] text-muted-foreground">{data.subtitle}</p>
            )}
          </div>

          <div className="rounded-xl bg-brand-50/60 px-5 py-4">
            <p className="flex items-baseline gap-2">
              <Price cents={sku?.price ?? data.fromPrice} className="text-[32px] text-brand-600" />
              {sku && <span className="text-[14px] text-muted-foreground">/{sku.unit}</span>}
              {sku?.listPrice && sku.listPrice > sku.price && (
                <span className="text-[14px] text-muted-foreground line-through">
                  {fen(sku.listPrice)}
                </span>
              )}
            </p>
          </div>

          {data.skus.length > 0 && (
            <div>
              <p className="mb-2 text-[14px] text-muted-foreground">规格</p>
              <div className="flex flex-wrap gap-2">
                {data.skus.map((s) => {
                  const label = Object.values(s.specs).join(" · ") || "默认";
                  const selected = s.id === sku?.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        setSkuId(s.id);
                        setQty(1);
                      }}
                      disabled={!s.available}
                      className={cn(
                        "rounded-lg border px-4 py-2.5 text-[14px] transition-colors",
                        selected
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : s.available
                            ? "border-border hover:border-brand-300"
                            : "border-border text-muted-foreground/50 line-through",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <span className="text-[14px] text-muted-foreground">数量</span>
            <QtyStepper value={qty} onChange={setQty} max={sku?.stock ?? 9999} />
            {sku && (
              <span className="text-[13px] text-muted-foreground">
                库存 {sku.stock} {sku.unit}
              </span>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            {previewing ? (
              /* 🔴 预览态不给死按钮，给一条出路 —— 点一下切回「我自己」接着买。 */
              <Button size="lg" className="flex-1" onClick={() => writeIdentity("")}>
                按我的进货价买
              </Button>
            ) : (
              /*
                🔴 **两个按钮**：加购是"继续逛"（建材一次买好几样），
                   立即下单是"这一件就够了"。
                ⚠️ 主次分开 —— 立即下单实心、加购描边，不用读完字就知道点哪个。
              */
              <>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1"
                  onClick={onAdd}
                  disabled={!canBuy}
                  loading={addToCart.isPending}
                >
                  {added ? "已加入 ✓" : canBuy ? "加入购物车" : "缺货"}
                </Button>
                <Button size="lg" className="flex-1" onClick={onBuyNow} disabled={!canBuy}>
                  立即下单
                </Button>
              </>
            )}
          </div>

          {/*
            ⚠️ 网页上有空间说清楚"买了之后会怎样" ——
               建材是大件、客单价高，这几条是下单前最常问的。
          */}
          <div className="space-y-2 border-t border-border pt-4 text-[13px] text-muted-foreground">
            <p className="flex items-center gap-2">
              <Truck className="size-4 shrink-0" />
              大件配送，下单后由供应商或你的专属服务商发货
            </p>
            <p className="flex items-center gap-2">
              <ShieldCheck className="size-4 shrink-0" />
              确认收货 15 天内可申请售后
            </p>
            <p className="flex items-center gap-2">
              <Store className="size-4 shrink-0" />
              线上下单，货款走线下渠道结算
            </p>
          </div>
        </div>
      </div>

      {/* ── 下：参数 / 安装 / 详情 ── */}
      <div className="mt-10 rounded-xl border border-border bg-card">
        <div className="flex border-b border-border">
          {TABS.map((t) => {
            const has =
              t.key === "attrs"
                ? Object.keys(data.attributes).length > 0
                : t.key === "install"
                  ? Boolean(data.installNote)
                  : /* ⚠️ 只有文字没有图、或只有图没有文字，「详情」这一栏都要出。 */
                    Boolean(data.descMd) || data.detailImageUrls.length > 0;
            if (!has) return null;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  "relative px-6 py-3.5 text-[15px] transition-colors",
                  tab === t.key
                    ? "font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                {tab === t.key && (
                  <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-brand-600" />
                )}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {tab === "attrs" && (
            /* ⚠️ 参数在网页上分两列：一列的话右边一大片空白，而参数常有十几条。 */
            <dl className="grid grid-cols-2 gap-x-10 gap-y-0 text-[14px]">
              {Object.entries(data.attributes).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border py-3">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-right">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {tab === "install" && (
            <p className="max-w-[60ch] text-[14px] leading-relaxed whitespace-pre-wrap text-muted-foreground">
              {data.installNote}
            </p>
          )}
          {tab === "desc" && (
            <>
              {/* ⚠️ 正文限宽 60 个字符 —— 1280 宽一行排下来眼睛会跟丢行。 */}
              {data.descMd && (
                <p className="max-w-[60ch] text-[14px] leading-relaxed whitespace-pre-wrap text-muted-foreground">
                  {data.descMd}
                </p>
              )}
              {/*
                ── 商品详情图 ──
                🔴 跟顶部轮播的 `imageUrls` 是两组：那边是"长什么样"，这里是"介绍商品"。
                ⚠️ **限宽 750px 居中**：详情图是按手机宽度做的竖长图，
                   在 1440 的屏上铺满整行会被拉伸得糊成一片（淘宝详情也是 750）。
                ⚠️ 不裁剪、不设固定高度 —— 长图切一刀就把中间的说明文字切没了。
              */}
              {data.detailImageUrls.length > 0 && (
                <div className={cn("mx-auto flex max-w-[750px] flex-col", data.descMd && "mt-6")}>
                  {data.detailImageUrls.map((url, i) => (
                    <img
                      key={url}
                      src={url}
                      alt={`详情图 ${i + 1}`}
                      loading="lazy"
                      className="w-full"
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DesktopShell>
  );
}
